import 'dotenv/config'
import React, { useEffect, useReducer } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useAsyncError } from 'react-router-dom'

import { Home } from './pages/Home'
import { History } from './pages/History'
import { Project } from './pages/Project'
import { Lab } from './pages/Lab'

import { FETCH_HISTORY_SUCCESS, historyReducer } from './history'
import { GENERATION_ERROR, GENERATION_SUCCESS, generationReducer } from './generation'
import { FINISH_LOADING, START_LOADING, loaderReducer } from './loader'
import { IMAGE_UPLOAD_ERROR, IMAGE_UPLOAD_SUCCESS, imageReducer } from './imageUploader'
import { CREATE_PROJECT_SUCCESS, FETCH_PROJECT_SUCCESS, DELETE_PROJECT_SUCCESS, CLONE_PROJECT_SUCCESS, CLEAR_PROJECT, UPDATE_PROJECT_ERROR, projectReducer } from './project'
import { TOGGLE_TO_IMAGE, TOGGLE_TO_VIDEO, toggleReducer } from './toggleMedia'
import { FETCH_VOICES_SUCCESS, SET_DRILLDOWN_LEVEL, SET_USER_ACTIVE_IN_EDITOR, SET_IS_CURSOR_AT_START, SET_FULLSCREEN, editorReducer, voiceReducer, drildownReducer } from './textEditor'
import { CLEAR_ERRORS, errorReducer } from './errors'
import { PrivateRoute } from './routes/PrivatRoute'
import { ErrorBoundary } from './errorBoundary/ErrorBoundary'
import { ErrorFallback } from './errorBoundary/ErrorFallback'
import { FIELD_UPDATE_SUCCESS, fieldUpdateReducer } from './fieldUpdateIndicator'
import { BurgerMenu } from './burgerMenu/BurgerMenu'
import { FETCH_VERSION_SUCCESS, fetchVersion, versionReducer } from './version'
import { AUTH_ERROR, AUTH_SUCCESS, checkAuth, authReducer } from './auth'

export const AppContext = React.createContext(null)

const initialState = {
  history: [], //   id, image_hash, project_id, speaking_rate, text, video_path
  projectsCount: 0,
  generationForm: { speakingRate: '', text: '', details: [] },
  loading: false,
  showVideo: false,
  voices: [],
  currentProject: {
    projectId: '',
    projectDataHash: '',
    imageHash: '',
    imageExtension: ''
  },
  editor: {
    updatedField: {},
    drilldownLevel: null,
    isUserActiveInEditor: false,
    isCursorAtStart: true,
    isFullscreen: false
  },
  isLoggedIn: null
}

const reducer = (state, action) => {
  switch (action.type) {
    case CREATE_PROJECT_SUCCESS:
    case FETCH_PROJECT_SUCCESS:
    case DELETE_PROJECT_SUCCESS:
    case CLONE_PROJECT_SUCCESS:
    case CLEAR_PROJECT:
    case UPDATE_PROJECT_ERROR:
      return projectReducer(state, action)

    case SET_USER_ACTIVE_IN_EDITOR:
    case SET_IS_CURSOR_AT_START:
    case SET_FULLSCREEN:
      return editorReducer(state, action)

    case GENERATION_SUCCESS:
    case GENERATION_ERROR:
      return generationReducer(state, action)

    case START_LOADING:
    case FINISH_LOADING:
      return loaderReducer(state, action)

    case IMAGE_UPLOAD_SUCCESS:
    case IMAGE_UPLOAD_ERROR:
      return imageReducer(state, action)

    case TOGGLE_TO_IMAGE:
    case TOGGLE_TO_VIDEO:
      return toggleReducer(state, action)

    case AUTH_SUCCESS:
    case AUTH_ERROR:
      return authReducer(state, action)

    case FETCH_HISTORY_SUCCESS:
      return historyReducer(state, action)

    case FETCH_VOICES_SUCCESS:
      return voiceReducer(state, action)

    case CLEAR_ERRORS:
      return errorReducer(state, action)

    case FIELD_UPDATE_SUCCESS:
      return fieldUpdateReducer(state, action)

    case SET_DRILLDOWN_LEVEL:
      return drildownReducer(state, action)

    case FETCH_VERSION_SUCCESS:
      return versionReducer(state, action)

    default:
      return state
  }
}

const App = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const throwAsyncError = useAsyncError()

  useEffect(() => {
    checkAuth(dispatch)
    fetchVersion(dispatch, throwAsyncError)
  }, [])

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <BrowserRouter>
        <ErrorBoundary
          FallbackComponent={ErrorFallback} onReset={() => {
            const navigate = useNavigate()
            navigate('/')
          }}
        >
          <div>
            <BurgerMenu />
          </div>
          <Routes>
            <Route
              path='/' element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route
              path='/project/:projectId'
              element={
                <PrivateRoute>
                  <Project />
                </PrivateRoute>
              }
            />
            <Route
              path='/history'
              element={
                <PrivateRoute>
                  <History />
                </PrivateRoute>
              }
            />
            <Route
              path='/lab'
              element={
                <PrivateRoute>
                  <Lab />
                </PrivateRoute>
              }
            />
            <Route path='*' element={<Navigate to='/' />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AppContext.Provider>
  )
}

export { App }
